import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { portfolio } from "@/api/portfolio";
import { Card, CardBody, CardHeader, EmptyState, Skeleton } from "@/components/ui";
import { Sparkline } from "@/components/Sparkline";
import { useWsSubscribe } from "@/ws/WebSocketProvider";

interface LivePnl {
  equity: number;
  daily_pnl: number;
  unrealized_pnl: number;
}

const SPARK_WINDOW = 60;

export function PortfolioPage() {
  const qc = useQueryClient();
  const [live, setLive] = useState<LivePnl | null>(null);
  const [equityHistory, setEquityHistory] = useState<number[]>([]);
  const [unrealizedHistory, setUnrealizedHistory] = useState<number[]>([]);

  useWsSubscribe("pnl_update", (msg) => {
    const equity = Number(msg.equity ?? 0);
    const unrealized = Number(msg.unrealized_pnl ?? 0);
    const daily = Number(msg.daily_pnl ?? 0);
    setLive({ equity, daily_pnl: daily, unrealized_pnl: unrealized });
    setEquityHistory((cur) => [...cur, equity].slice(-SPARK_WINDOW));
    setUnrealizedHistory((cur) => [...cur, unrealized].slice(-SPARK_WINDOW));
  });
  useWsSubscribe("fill", () => {
    qc.invalidateQueries({ queryKey: ["portfolio"] });
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ["portfolio"],
    queryFn: () => portfolio.get(),
    refetchInterval: 30_000,
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <h1 className="text-base font-semibold text-term-fg">Portfolio</h1>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardBody>
                <Skeleton className="mb-2 h-3 w-16" />
                <Skeleton className="h-6 w-24" />
              </CardBody>
            </Card>
          ))}
        </div>
        <Card>
          <CardBody>
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="mb-2 h-5 w-full" />
            ))}
          </CardBody>
        </Card>
      </div>
    );
  }
  if (error) return <div className="text-xs down">{String(error)}</div>;
  if (!data) return null;

  const s = data.summary;
  const equity = live?.equity ?? s.equity;
  const unrealized = live?.unrealized_pnl ?? s.total_unrealized_pnl;
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-base font-semibold text-term-fg">Portfolio</h1>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Stat label="Equity" value={fmtMoney(equity)} spark={equityHistory} />
        <Stat label="Cash" value={fmtMoney(s.cash)} />
        <Stat label="Buying power" value={fmtMoney(s.buying_power)} />
        <Stat
          label="Unrealized P&L"
          value={fmtMoney(unrealized)}
          tone={unrealized >= 0 ? "up" : "down"}
          spark={unrealizedHistory}
          sparkTone={unrealized >= 0 ? "up" : "down"}
        />
      </div>

      <Card>
        <CardHeader>Positions ({data.positions.length})</CardHeader>
        <CardBody className="p-0">
          {data.positions.length === 0 ? (
            <EmptyState
              title="No open positions"
              description="Place an order from the Terminal to take a position."
            />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left font-medium uppercase tracking-wider text-2xs">Sym</th>
                  <th className="px-3 py-1.5 text-left font-medium uppercase tracking-wider text-2xs">Side</th>
                  <th className="px-3 py-1.5 text-right font-medium uppercase tracking-wider text-2xs">Qty</th>
                  <th className="px-3 py-1.5 text-right font-medium uppercase tracking-wider text-2xs">Avg</th>
                  <th className="px-3 py-1.5 text-right font-medium uppercase tracking-wider text-2xs">UPnL</th>
                  <th className="px-3 py-1.5 text-right font-medium uppercase tracking-wider text-2xs">RPnL</th>
                </tr>
              </thead>
              <tbody>
                {data.positions.map((p) => (
                  <tr key={p.instrument_id} className="border-t border-term-border hover:bg-term-elev">
                    <td className="px-3 py-1.5 font-medium text-term-fg">{p.symbol}</td>
                    <td className={`px-3 py-1.5 ${p.side === "BUY" ? "up" : "down"}`}>{p.side}</td>
                    <td className="px-3 py-1.5 text-right tnum text-term-dim">{p.quantity}</td>
                    <td className="px-3 py-1.5 text-right tnum text-term-dim">{p.avg_entry_price.toFixed(2)}</td>
                    <td className={`px-3 py-1.5 text-right tnum ${p.unrealized_pnl >= 0 ? "up" : "down"}`}>
                      {p.unrealized_pnl.toFixed(2)}
                    </td>
                    <td className={`px-3 py-1.5 text-right tnum ${p.realized_pnl >= 0 ? "up" : "down"}`}>
                      {p.realized_pnl.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
  spark,
  sparkTone,
}: {
  label: string;
  value: string;
  tone?: "up" | "down";
  spark?: number[];
  sparkTone?: "up" | "down";
}) {
  return (
    <Card>
      <CardBody>
        <div className="flex items-baseline justify-between">
          <span className="text-2xs uppercase tracking-wider text-term-mute">{label}</span>
          {spark && spark.length >= 2 && (
            <Sparkline data={spark} tone={sparkTone ?? "auto"} width={56} height={16} />
          )}
        </div>
        <div className={`mt-1 text-xl font-semibold tnum ${tone === "up" ? "up" : tone === "down" ? "down" : "text-term-fg"}`}>
          {value}
        </div>
      </CardBody>
    </Card>
  );
}

function fmtMoney(n: number): string {
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });
}
